import React, { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils.js";
import { TONES, type StatusTone } from "../../lib/tones.js";
import { shapeClass, type Shape } from "../../lib/shape.js";
import {
  CATEGORICAL_TINTS,
  MODULE_TINT_SOFT,
  type CategoricalTint,
  type ModuleTint,
} from "../../lib/tints.js";

/* IconTile — a tinted square or disc around a glyph (C7, wave 3
   design-system programme). EmptyState's medallion, AppLauncher's module tile
   and ModuleHomeHeader's mark are today's private versions; none of them is
   edited here, so their pictures are unchanged. Every colour comes from
   lib/tones.ts or lib/tints.ts: this file holds no hue map. */

/** 24/32/40/48px: the square-media ramp shared with Avatar and the
 *  EmptyState medallion. */
export type IconTileSize = "xs" | "sm" | "md" | "lg";

/** `rounded` (default) is the 8px corner at every size; `circle` is a disc. */
export type IconTileShape = Extract<Shape, "rounded" | "circle">;

/** `soft` (default): fill, border and glyph colour. `outline`: border and
 *  glyph only, on a transparent fill. */
export type IconTileVariant = "soft" | "outline";

/** Decorative identity. A `ModuleTint` is for chrome only (module marks,
 *  launcher tiles); a tile inside content takes a `CategoricalTint`
 *  (ADR-0092). */
export type IconTileTint = ModuleTint | CategoricalTint;

export const iconTileVariants = cva("flex items-center justify-center border", {
  variants: {
    size: {
      xs: "w-6 h-6",
      sm: "w-8 h-8",
      md: "w-10 h-10",
      lg: "w-12 h-12",
    },
    shape: {
      rounded: shapeClass("rounded"),
      circle: shapeClass("circle"),
    },
    variant: {
      soft: "",
      outline: "bg-transparent",
    },
  },
  defaultVariants: { size: "md", shape: "rounded", variant: "soft" },
});

/* The glyph step for each size: a bare svg child at 14/16/20/24px, and 1-2
   letters in a matching type step. It sits on a box-less (`contents`)
   wrapper, so the tile's own classes stay the box recipe alone (EmptyState's
   medallion set, exactly) and the wrapper never changes the layout. */
const GLYPH: Record<IconTileSize, string> = {
  xs: "contents [&>svg]:size-3.5 text-dense-2 font-medium leading-none",
  sm: "contents [&>svg]:size-4 text-xs font-medium leading-none",
  md: "contents [&>svg]:size-5 text-sm font-medium leading-none",
  lg: "contents [&>svg]:size-6 text-base font-medium leading-none",
};

interface IconTileBaseProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "role" | "aria-hidden"
> {
  size?: IconTileSize | undefined;
  shape?: IconTileShape | undefined;
  variant?: IconTileVariant | undefined;
  /** A bare icon or 1-2 letters. Decorative (`aria-hidden`) unless the tile
   *  is named with `aria-label` (or `aria-labelledby`), which makes it
   *  `role="img"`. */
  children?: React.ReactNode;
}

/** A semantically toned tile (the default is `neutral`). */
export interface IconTileToneProps extends IconTileBaseProps {
  tone?: StatusTone | undefined;
  /** A tint is decorative and never joins a tone. */
  tint?: undefined;
}

/** A decoratively tinted tile. Never with a tone. */
export interface IconTileTintProps extends IconTileBaseProps {
  tint: IconTileTint;
  tone?: undefined;
}

export type IconTileProps = IconTileToneProps | IconTileTintProps;

/* Fill, border and glyph colour. soft = the recipe's fill and border with its
   text colour on the glyph; outline = the same without the fill. */
function colourClasses(
  variant: IconTileVariant,
  tone: StatusTone,
  tint: IconTileTint | undefined,
) {
  const soft = variant === "soft";
  if (tint === undefined) {
    const recipe = TONES[tone];
    return soft ? [recipe.soft, recipe.text] : [recipe.border, recipe.text];
  }
  if (typeof tint === "number") {
    // The Badge tint recipe: the text stays pb-text, so contrast never rests
    // on a decorative hue.
    const recipe = CATEGORICAL_TINTS[tint];
    return [soft && recipe.fill, recipe.line, "text-pb-text"];
  }
  const recipe = MODULE_TINT_SOFT[tint];
  return [soft && recipe.fill, recipe.border, recipe.text];
}

export const IconTile = forwardRef<HTMLSpanElement, IconTileProps>(
  function IconTile(
    {
      size = "md",
      shape = "rounded",
      variant = "soft",
      tone = "neutral",
      tint,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const labelled = Boolean(rest["aria-label"] || rest["aria-labelledby"]);
    return (
      <span
        ref={ref}
        className={cn(
          iconTileVariants({ size, shape, variant }),
          colourClasses(variant, tone, tint),
          className,
        )}
        {...rest}
        role={labelled ? "img" : undefined}
        aria-hidden={labelled ? undefined : true}
      >
        <span className={GLYPH[size]}>{children}</span>
      </span>
    );
  },
);
